import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { useMyPermissions } from '@/hooks/usePermissions';
import { requiredPermissionsForPath } from '@/components/sidebar/permissionFilter';
import { menuItems } from '@/components/sidebar/sidebarConfig';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Garde de pages par permissions (flag opt-in `garde_routes_permissions`).
 * Sans lui, une page masquée du menu restait ouverte en tapant son adresse.
 * Admins et super_admin : aucune restriction. Échec du chargement des permissions :
 * page autorisée (même règle que le menu). Refus → /dashboard, qui affiche le menu
 * neutre (UserHub) pour les rôles sans `reports`.
 */
export function RoutePermissionGuard({ children }: { children: ReactNode }) {
  const { userRole, tenantId } = useAuth();
  const { isFeatureEnabled } = useFeatureToggles();
  const { pathname } = useLocation();
  const enabled = isFeatureEnabled('garde_routes_permissions');
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const required = enabled && !isAdmin ? requiredPermissionsForPath(pathname, menuItems) : null;
  return (
    <PermissionCheck required={required} tenantId={tenantId}>
      {children}
    </PermissionCheck>
  );
}

function PermissionCheck({
  required,
  tenantId,
  children,
}: {
  required: ReturnType<typeof requiredPermissionsForPath>;
  tenantId: string | null | undefined;
  children: ReactNode;
}) {
  const { can, loading, loaded } = useMyPermissions();
  if (!required || !tenantId) return <>{children}</>;
  if (loading && !loaded) return <LoadingSpinner />;
  if (loaded && !required.some(can)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
